import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/Button';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag } from 'lucide-react';
export function CartPage() {
  const {
    items,
    removeItem,
    updateQuantity,
    totalPrice,
    totalItems
  } = useCart();
  return <div className="min-h-screen bg-uncommon-black flex flex-col" data-id="element-181">
      <Header data-id="element-182" />

      <main className="flex-grow pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-id="element-183">
        <div className="max-w-7xl mx-auto" data-id="element-184">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-185">
            YOUR <span className="text-uncommon-gold" data-id="element-186">C</span>ART
          </h1>

          {items.length === 0 ? <div className="text-center py-24 border border-uncommon-grey rounded-lg bg-uncommon-dark" data-id="element-187">
              <ShoppingBag size={64} className="mx-auto text-uncommon-grey mb-6" data-id="element-188" />
              <h2 className="text-2xl font-bold text-white mb-4" data-id="element-189">
                Your cart is empty
              </h2>
              <p className="text-gray-400 mb-8" data-id="element-190">
                Looks like you haven't added any gear yet.
              </p>
              <Link to="/" data-id="element-191">
                <Button variant="primary" data-id="element-192">Start Shopping</Button>
              </Link>
            </div> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-193">
              {/* Cart Items List */}
              <div className="lg:col-span-2 space-y-6" data-id="element-194">
                <AnimatePresence data-id="element-195">
                  {items.map(item => <motion.div key={item.id} layout initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                x: -100
              }} className="flex flex-col sm:flex-row gap-6 p-6 bg-uncommon-dark border border-uncommon-grey rounded-lg" data-id="element-196">
                      {/* Product Image */}
                      <div className="w-full sm:w-32 aspect-square bg-uncommon-black rounded overflow-hidden flex-shrink-0" data-id="element-197">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-198" />
                      </div>

                      {/* Product Details */}
                      <div className="flex-grow flex flex-col justify-between" data-id="element-199">
                        <div className="flex justify-between items-start mb-4" data-id="element-200">
                          <div data-id="element-201">
                            <p className="text-xs text-uncommon-gold uppercase tracking-wider mb-1" data-id="element-202">
                              {item.category}
                            </p>
                            <h3 className="text-xl font-bold text-white" data-id="element-203">
                              {item.name}
                            </h3>
                          </div>
                          <button onClick={() => removeItem(item.id)} className="text-gray-500 hover:text-red-500 transition-colors p-1" aria-label="Remove item" data-id="element-204">
                            <Trash2 size={20} data-id="element-205" />
                          </button>
                        </div>

                        <div className="flex justify-between items-end" data-id="element-206">
                          {/* Quantity Controls */}
                          <div className="flex items-center border border-uncommon-grey rounded bg-black" data-id="element-207">
                            <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="p-2 text-white hover:text-uncommon-gold transition-colors" data-id="element-208">
                              <Minus size={16} data-id="element-209" />
                            </button>
                            <span className="w-10 text-center text-white font-medium" data-id="element-210">
                              {item.quantity}
                            </span>
                            <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-2 text-white hover:text-uncommon-gold transition-colors" data-id="element-211">
                              <Plus size={16} data-id="element-212" />
                            </button>
                          </div>

                          {/* Price */}
                          <div className="text-right" data-id="element-213">
                            <p className="text-gray-400 text-sm mb-1" data-id="element-214">
                              ${item.price.toFixed(2)} x {item.quantity}
                            </p>
                            <p className="text-xl font-bold text-white" data-id="element-215">
                              ${(item.price * item.quantity).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>)}
                </AnimatePresence>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1" data-id="element-216">
                <div className="bg-uncommon-dark border border-uncommon-grey rounded-lg p-8 sticky top-32" data-id="element-217">
                  <h2 className="text-2xl font-bold text-white mb-6" data-id="element-218">
                    Order Summary
                  </h2>

                  <div className="space-y-4 mb-8" data-id="element-219">
                    <div className="flex justify-between text-gray-300" data-id="element-220">
                      <span data-id="element-221">Subtotal ({totalItems} items)</span>
                      <span data-id="element-222">${totalPrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-300" data-id="element-223">
                      <span data-id="element-224">Shipping</span>
                      <span data-id="element-225">{totalPrice > 50 ? 'FREE' : '$10.00'}</span>
                    </div>
                    <div className="flex justify-between text-gray-300" data-id="element-226">
                      <span data-id="element-227">Tax (Est.)</span>
                      <span data-id="element-228">${(totalPrice * 0.08).toFixed(2)}</span>
                    </div>

                    <div className="border-t border-uncommon-grey pt-4 mt-4" data-id="element-229">
                      <div className="flex justify-between text-white font-bold text-xl" data-id="element-230">
                        <span data-id="element-231">Total</span>
                        <span data-id="element-232">
                          $
                          {(totalPrice * 1.08 + (totalPrice > 50 ? 0 : 10)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button variant="primary" fullWidth className="mb-4" data-id="element-233">
                    Checkout <ArrowRight size={18} className="ml-2" data-id="element-234" />
                  </Button>

                  <p className="text-xs text-center text-gray-500" data-id="element-235">
                    Secure checkout powered by Stripe.
                    <br data-id="element-236" />
                    Free returns within 30 days.
                  </p>
                </div>
              </div>
            </div>}
        </div>
      </main>

      <Footer data-id="element-237" />
    </div>;
}